import { useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { ApiError, createApi, uploadToS3 } from "../api/client";
import { usePricing } from "../hooks/usePricing";
import { PrimaryButton, Field, ErrorText } from "./_auth_ui";
import { PriceAnchor } from "../components/PriceAnchor";
import { TrustStrip } from "../components/TrustStrip";

export function Upload() {
  const { getIdToken } = useAuth();
  const { display: price } = usePricing();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [subjectHint, setSubjectHint] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rejection, setRejection] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const api = createApi(getIdToken);

  const pick = (f: File | null) => {
    setFile(f);
    setPreview(f ? URL.createObjectURL(f) : null);
    setRejection(null);
    setError(null);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true); setError(null); setRejection(null);
    try {
      setStatus("Requesting upload link…");
      const { photo_key, upload_url } = await api.requestUpload(file.type);

      setStatus("Uploading your photo…");
      await uploadToS3(upload_url, file);

      setStatus("Checking your photo…");
      const { checkout_url } = await api.createJob(photo_key, subjectHint || undefined);

      setStatus("Redirecting to checkout…");
      window.location.href = checkout_url;
    } catch (err: unknown) {
      if (err instanceof ApiError && err.code === "content_rejected") {
        setRejection(err.message);
        setFile(null);
        setPreview(null);
      } else {
        setError(err instanceof Error ? err.message : "Something went wrong");
      }
      setStatus(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mx-auto max-w-2xl px-6 py-12">
      <h1 className="font-serif text-4xl tracking-tight">Upload your photo</h1>
      <p className="mt-2 text-neutral-600">
        Best results with a clear scan of the original. We'll restore damage and add subtle motion.
      </p>

      {rejection && (
        <div
          role="alert"
          className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5"
        >
          <p className="font-serif text-lg text-neutral-900">
            We can't use this one.
          </p>
          <p className="mt-1 text-sm text-neutral-700">{rejection}</p>
          <p className="mt-3 text-sm text-neutral-600">
            Please pick a different photo or edit your description.
            <span className="block mt-1 text-neutral-500">
              You haven't been charged — try another and continue.
            </span>
          </p>
        </div>
      )}

      <form onSubmit={onSubmit} className="mt-8 space-y-6">
        <label
          className={`block rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition ${
            preview ? "border-neutral-300 bg-white" : "border-neutral-300 bg-neutral-50 hover:bg-white"
          }`}
        >
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/avif"
            onChange={(e) => pick(e.target.files?.[0] ?? null)}
            className="hidden"
          />
          {preview ? (
            <img src={preview} className="mx-auto max-h-80 rounded-lg object-contain" alt="preview" />
          ) : (
            <>
              <p className="font-serif text-xl">Drop a photo here</p>
              <p className="mt-1 text-sm text-neutral-500">or click to browse</p>
            </>
          )}
        </label>

        {file && (
          <p className="text-xs text-neutral-500 text-center">
            {file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB
          </p>
        )}

        <Field
          label="Who or when is this? (optional)"
          value={subjectHint}
          onChange={setSubjectHint}
          placeholder="e.g. Grandma Irene, 1954 wedding"
        />

        <ErrorText error={error} />
        {status && <p className="text-sm text-neutral-600 text-center">{status}</p>}

        <div className="flex justify-center">
          <PriceAnchor align="center" />
        </div>

        <PrimaryButton loading={busy} disabled={!file}>
          Continue to payment — {price}
        </PrimaryButton>

        <TrustStrip />
      </form>
    </section>
  );
}
